/**
 * Mutation hook for printing home visit patient schedule as PDF (一覧印刷).
 *
 * Opens PDF in a new tab. Follows the same pattern as usePrintWeeklyScheduleMutation.
 */

import { useMutation } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import useAppModal from '@/hooks/useAppModal';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { printHomeVisitPatients } from '@/services/main/homeVisitPatient';
import type { PrintHomeVisitPatientPayload } from '@/pages/main/homeVisitPatient/types';
import { getApiErrorMessageAsync } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';

function getServiceTypeFallback(): string {
  return typeof localStorage !== 'undefined'
    ? localStorage.getItem('service_type') || SERVICE_TYPE_A2
    : SERVICE_TYPE_A2;
}

export function usePrintHomeVisitPatientMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const serviceType =
    theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
      ? theme.serviceType
      : getServiceTypeFallback();
  const modal = useAppModal();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    PrintHomeVisitPatientPayload
  >({
    mutationFn: payload => printHomeVisitPatients(tenant, clinicNumber, payload, serviceType),
  });

  const triggerPrint = async (payload: PrintHomeVisitPatientPayload) => {
    // Open blank tab synchronously to avoid popup blocking
    const tab = window.open('', '_blank');

    try {
      const blob = await mutateAsync(payload);
      const url = URL.createObjectURL(blob);

      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      tab?.close();
      const message = await getApiErrorMessageAsync(error, '印刷に失敗しました。');
      modal.error({ title: 'エラー', content: message, okText: 'OK' });
      
    }
  };

  return { triggerPrint, isLoading };
}
